// Signing in with Google, Apple or Facebook, the account's list of ways in, and the white-labeled host's choice of them.
import { z } from "zod";
import { AccountSignedIn, SignInCodeAccepted } from "./account";
import { IsoDateTime, Uuid } from "./common";
import { UiLang } from "./identity";
import { SignupSource } from "./signup-source";

const Email = z.string().trim().min(3).max(254).email();

export const LOGIN_PROVIDERS = ["google", "apple", "facebook"] as const;
export const LoginProvider = z.enum(LOGIN_PROVIDERS);
export type LoginProvider = z.infer<typeof LoginProvider>;

/** Every way in: the e-mail code and the three providers. */
export const LOGIN_METHODS = ["email", ...LOGIN_PROVIDERS] as const;
export const LoginMethod = z.enum(LOGIN_METHODS);
export type LoginMethod = z.infer<typeof LoginMethod>;

/** How long a trip to a provider may take, from the button to the way back. */
export const OAUTH_ATTEMPT_TTL_MINUTES = 10;
/** A session signed in longer ago than this asks for the e-mailed code before it adds or removes a way in. */
export const LOGIN_REAUTH_MINUTES = 10;

/** The error codes the way back from a provider may carry; each has its own message in four languages. */
export const OAUTH_ERROR_CODES = ["oauth_cancelled", "oauth_expired", "oauth_address_refused", "oauth_provider_unavailable"] as const;

const SignInId = SignInCodeAccepted.shape.signInId;

export const OAuthStartRequest = z.strictObject({ lang: UiLang.default("en") });
export type OAuthStartRequest = z.input<typeof OAuthStartRequest>;

/** s10-site-a: a sign-in's trip also carries where the person came from, kept on the trip and written once if it makes the account. */
export const OAuthSignInStartRequest = z.strictObject({ lang: UiLang.default("en"), source: SignupSource.optional() });
export type OAuthSignInStartRequest = z.input<typeof OAuthSignInStartRequest>;

/** The tab keeps `signInId` and goes to `authorizeUrl`. Only that tab can finish what it started. */
export const OAuthStarted = z.object({ signInId: SignInId, authorizeUrl: z.string().url() });
export type OAuthStarted = z.infer<typeof OAuthStarted>;

/**
 * The page the provider's way back lands on. Every provider returns to the API, which keeps the code (or the provider's error) on the
 * trip and sends the page the `state`, with a one-time `secret` in the address's #fragment. The page sends the three together: the tab's
 * `signInId`, the `state` and the `secret`. The code never reaches the page.
 */
export const OAuthCompleteRequest = z.strictObject({
  signInId: SignInId,
  state: z.string().trim().min(16).max(256),
  secret: z.string().trim().min(16).max(256),
});
export type OAuthCompleteRequest = z.input<typeof OAuthCompleteRequest>;

/**
 * What the way back ends in:
 * - `signed_in`: the session, as the code gives it;
 * - `linked`: the method was added to the account that started it from „Metode de autentificare”;
 * - `confirm_email`: a first Facebook sign-in: a code went to the address Facebook gave, account or not; type it with this `signInId`;
 * - `email_required`: Facebook gave no address; send one with `POST /v1/public/account/oauth/email`, then type its code.
 */
export const OAuthCompleted = z.discriminatedUnion("status", [
  z.object({ status: z.literal("signed_in"), signedIn: AccountSignedIn }),
  z.object({ status: z.literal("linked"), method: LoginProvider }),
  z.object({ status: z.literal("confirm_email"), signInId: SignInId, email: z.string() }),
  z.object({ status: z.literal("email_required"), signInId: SignInId }),
]);
export type OAuthCompleted = z.infer<typeof OAuthCompleted>;

/** The address a Facebook sign-in without one goes on with, with the bot check's answer as for the code. The answer is the code step's `signInId`. */
export const OAuthEmailRequest = z.strictObject({ signInId: SignInId, email: Email, lang: UiLang.default("en"), turnstileToken: z.string().trim().min(1).max(2048).optional() });
export type OAuthEmailRequest = z.input<typeof OAuthEmailRequest>;

// ---------------------------------------------------------------------------
// „Contul tău → Metode de autentificare”
// ---------------------------------------------------------------------------

export const AccountMethodDto = z.object({
  id: Uuid,
  method: LoginMethod,
  /** The address the method gave, which for Apple may be a relay. Null when Facebook gave none. */
  email: z.string().nullable(),
  createdAt: IsoDateTime,
  lastUsedAt: IsoDateTime.nullable(),
  /** The account's own address (its oldest e-mail identity): it cannot be removed, so the code always reaches the account. */
  accountAddress: z.boolean(),
});
export type AccountMethodDto = z.infer<typeof AccountMethodDto>;

export const AccountMethodList = z.object({
  items: z.array(AccountMethodDto),
  /** The providers this host offers, which can be added. */
  available: z.array(LoginProvider),
  /** Until when adding or removing needs no code. Null: the code is asked first. */
  freshUntil: IsoDateTime.nullable(),
});
export type AccountMethodList = z.infer<typeof AccountMethodList>;

export const AccountReauthRequest = z.strictObject({ lang: UiLang.default("en") });
export type AccountReauthRequest = z.input<typeof AccountReauthRequest>;

export const AccountReauthVerify = z.strictObject({
  signInId: SignInId,
  code: z.string().trim().regex(/^\d{6}$/, "sign_in_code_format"),
});
export type AccountReauthVerify = z.input<typeof AccountReauthVerify>;

// ---------------------------------------------------------------------------
// The host's choice (decision 31), set by an operator in the admin
// ---------------------------------------------------------------------------

export const HostLoginProviderDto = z.object({
  provider: LoginProvider,
  enabled: z.boolean(),
  /** Google's client id, Apple's Services ID, Facebook's app id. Not secret. */
  clientId: z.string().nullable(),
  /** Apple only. */
  teamId: z.string().nullable(),
  keyId: z.string().nullable(),
  /** Apple only, optional: the primary App ID, which Apple's server notices may name instead of the Services ID. */
  appId: z.string().nullable(),
  /** The last 4 characters of the stored secret; the secret itself never leaves the API. */
  secretLast4: z.string().nullable(),
  /** Whether every field the provider needs is filled. */
  complete: z.boolean(),
  /** The address the host registers with the provider as the way back. */
  callbackUrl: z.string(),
  /** Apple's server-to-server notifications and Facebook's data deletion, where the provider asks for one. */
  notificationUrl: z.string().nullable(),
});
export type HostLoginProviderDto = z.infer<typeof HostLoginProviderDto>;

export const HostLoginSettingsDto = z.object({
  hostId: Uuid,
  /** The host's own domain, where its login screen is. Null: none set, so nobody signs in on it. */
  domain: z.string().nullable(),
  /** The bot check on the host's domain, once the operator has added the domain to the Turnstile widget. */
  botCheck: z.boolean(),
  /** Whether the service holds the bot check's keys at all. */
  botCheckAvailable: z.boolean(),
  providers: z.array(HostLoginProviderDto),
});
export type HostLoginSettingsDto = z.infer<typeof HostLoginSettingsDto>;

const Field = z.string().trim().min(1).max(256);

export const HostLoginProviderPut = z.strictObject({
  provider: LoginProvider,
  enabled: z.boolean(),
  /** Absent keeps what is stored; null clears it. */
  clientId: Field.nullable().optional(),
  teamId: Field.nullable().optional(),
  keyId: Field.nullable().optional(),
  appId: Field.nullable().optional(),
  /** Write-only: the client secret, the app secret or Apple's .p8 key. Absent keeps the stored one; null removes it. */
  secret: z.string().trim().min(1).max(8192).nullable().optional(),
});
export type HostLoginProviderPut = z.input<typeof HostLoginProviderPut>;

export const HostLoginSettingsPut = z.strictObject({
  botCheck: z.boolean().optional(),
  providers: z.array(HostLoginProviderPut).max(LOGIN_PROVIDERS.length).optional(),
});
export type HostLoginSettingsPut = z.input<typeof HostLoginSettingsPut>;

// ---------------------------------------------------------------------------
// A provider's request to delete what we hold from it (Facebook's data deletion callback)
// ---------------------------------------------------------------------------

/** What the status page Meta links to shows: the confirmation code, and that the identity is gone. */
export const AccountDeletionStatus = z.object({
  code: z.string(),
  provider: LoginProvider,
  status: z.enum(["completed"]),
  requestedAt: IsoDateTime,
});
export type AccountDeletionStatus = z.infer<typeof AccountDeletionStatus>;

/** Apple's server-to-server notification: one JWT, signed by Apple, whose `events` claim says what happened. */
export const AppleLoginNotification = z.object({ payload: z.string().min(1).max(16_384) });
export type AppleLoginNotification = z.infer<typeof AppleLoginNotification>;

/** The data deletion callback's answer, in the shape Meta reads: where the person can check, and the code they are shown. */
export const FacebookDeletionAnswer = z.object({ url: z.string().url(), confirmation_code: z.string() });
export type FacebookDeletionAnswer = z.infer<typeof FacebookDeletionAnswer>;
